import {
  ATypeMainHead,
  ATypeCardView,
  BTypeBreadCrumb,
  colors,
  ATypeMap,
  FullWidthTwinButton,
  ATypeNationalMeritSummary,
  ATypeNationalMeritDescription,
  GOI_WEB_URL,
  customEvent,
  useCrematoriumInquiryModal,
  getCrematoriumPhoneByCompanyname,
} from "goi_common";
import { GetStaticPaths, GetStaticProps } from "next";

import React from "react";
import { ParlorDetailProps } from "goi_common";
import { parlorRegion } from "@/constants/parlorRegion";
import { Box, Stack } from "@mui/material";
import Head from "next/head";

import crematoriums from "goi_common/src/constants/hwajang_detail.json";

const { regionColor, targetSidos } = parlorRegion;

export const getStaticPaths: GetStaticPaths = async () => {
  const paths = crematoriums
    .filter((p) => targetSidos.includes(String(p.sido)))
    .map((crema) => ({
      params: {
        companyname: crema.companyname,
      },
    }));

  return {
    paths,
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const companyname = params?.companyname;
  let crematorium;
  let district;
  let name = "";
  let description = "";

  if (companyname) {
    crematorium = crematoriums.find(
      (crema) => crema.companyname === companyname
    );
    if (crematorium) {
      name = crematorium.companyname;

      district = crematorium.address.slice(0, 2);
      if (crematorium.address.split(" ")[0].length == 4)
        district = crematorium.address[0] + crematorium.address[2];

      description = `대구경북 지역 국가유공자 장례 혜택 및 안내. 대구경북 지역 국가유공자 무료 화장시설. 대구경북 지역 국가유공자 장례 혜택 시 필요 서류. 대구경북 지역 국가유공자 장례 관련 상담. `;
    }
  }

  return {
    props: {
      defaultCrema: crematorium,
      pageDescription: description,
      pageKeywords: `${crematorium?.companyname}, ${crematorium?.companyname} 국가유공자, ${district} 국가유공자 장례, 국가유공자 장례 얼마,`,
      pageOgTitle: `${crematorium?.companyname} 국가유공자 화장 비용 안내 | 대구경북장례정보포털`,
      pageOgDescription: description,
      pageOgImage: crematorium?.images[0].url || "",
      hasError: !crematorium,
    },
  };
};

export default function NationalMeritCrematoriumPage({
  defaultCrema,
  pageOgTitle,
}: {
  defaultCrema: ParlorDetailProps;
  pageOgTitle: string;
}) {
  const regionCrematoriums = crematoriums.filter((p) =>
    targetSidos.includes(String(p.sido))
  );

  /** 문의 모달의 개장·이장 / 고인 확인·방문이 거는 번호. 원본에 없으면 null 이고 고이로 폴백한다. */
  const facilityPhone = getCrematoriumPhoneByCompanyname(
    defaultCrema.companyname
  );
  const { openInquiryModal } = useCrematoriumInquiryModal({
    eventDomain: "dggb",
    eventPath: "nat_merit_crema",
  });
  const carouselItems = regionCrematoriums.map((res, i) => {
    return {
      "@type": "ListItem",
      name: `(비용안내) ${res.companyname}`,
      image: `${res.images[0].url}`,
      url: `https://dggb-portal.funeralblog.co.kr/national-merit/crematorium/${res.companyname}/`,
      position: i + 1,
    };
  });

  const carouselRichSearch =
    regionCrematoriums.length > 6
      ? {
          "@context": "http://schema.org",
          "@type": "ItemList",
          itemListElement: carouselItems,
        }
      : {};
  return (
    <Stack>
      <Head>
        <title>{pageOgTitle}</title>
        {regionCrematoriums.length > 5 && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(carouselRichSearch),
            }}
          />
        )}
      </Head>

      <BTypeBreadCrumb
        firstItem={{
          text: `대구 경북 국가유공자 장례헬프센터`,
          href: `/national-merit/`,
          event: {
            domain: "dggb",
            path: "nat_merit_crema",
            trigger_and_target: "br_crumb_nat_merit",
          },
        }}
        goiHomeEvent={{
          domain: "dggb",
          path: "nat_merit_crema",
          trigger_and_target: "br_crumb_goi_home",
        }}
      />
      <ATypeMainHead
        defaultParlor={defaultCrema}
        title={`${defaultCrema.companyname}`}
        managed_type={defaultCrema.managed_type}
        address={defaultCrema.address}
      />

      <Box style={{ padding: "12px 20px" }} className="subtitle_21_b">
        {defaultCrema.companyname}
        <br />
        국가유공자에게 제공되는 혜택
      </Box>

      <Stack padding="20px 20px 40px" gap="24px">
        <ATypeNationalMeritDescription />
        <ATypeNationalMeritSummary mainColor={regionColor} />
        <FullWidthTwinButton
          firstBtn={{
            isGoiCall: true,
            text: "상담하기",
            onClick: () => {
              openInquiryModal(facilityPhone);
              customEvent({
                domain: "dggb",
                path: "nat_merit_crema",
                trigger_and_target: "goi_call",
              });
            },
          }}
          secondBtn={{
            text: "비용 알아보기",
            href: `${GOI_WEB_URL}?utm_source=dogsound` + "/match/request/?utm_source=dogsound",
            onClick: () => {
              customEvent({
                domain: "dggb",
                path: "nat_merit_crema",
                trigger_and_target: "uv_mth_req",
              });
            },
          }}
          backgroundColor={regionColor.c300}
        />
        <Box className="subtitle_14_b" textAlign="center">
          상담을 통해 자세한 국가 유공자 장례 혜택을 안내드립니다.
        </Box>
      </Stack>

      <Stack
        style={{ background: regionColor.c100 }}
        padding="40px 20px"
        marginBottom="40px"
        gap="8px"
      >
        <ATypeMap defaultParlor={defaultCrema} />
      </Stack>
      <Stack padding="0px 20px" marginBottom="40px" gap="8px">
        <h3 className="button_16_sb">대구 · 경북 지역 화장장</h3>
        {regionCrematoriums.map((res) => (
          <ATypeCardView
            key={res.companyname}
            name={res.companyname}
            href={`/national-merit/crematorium/${res.companyname}/`}
            descriptions={["국가유공자 무료"]}
            imgSrc={res.images[0].url ? res.images[0].url : ""}
            event={{
              domain: "dggb",
              path: "nat_merit_crema",
              trigger_and_target: "uv_crema_detail",
            }}
          />
        ))}
      </Stack>
    </Stack>
  );
}
